import { useInjection } from 'inversify-react';
import { useEffect, useState } from 'react';
import { WIDGET } from '../constants/WIDGET';
import { Widget } from '../types/widget';
import useBasketSize from './useBasketSize';
import { ZOOM_MODULE_IDS } from '../lib/widget/zoom/zoom.types';
import { STATE_MODULE_IDS } from '../lib/widget/state/state.types';
import type { IZoomController } from '../lib/widget/zoom/zoom.interface';
import type { IStateController } from '../lib/widget/state/state.interface';

const useZoom = () => {
  const [zoomState, setZoomState] = useState({
    zoomIsMaximum: false,
    zoomIsMinimum: true,
    zoomIndex: 0,
  });
  const basketSize = useBasketSize();
  const Widget = useInjection<Widget>(WIDGET);
  const zoomController: IZoomController = useInjection(ZOOM_MODULE_IDS.ZOOM_CONTROLLER);
  const stateController: IStateController = useInjection(STATE_MODULE_IDS.STATE_CONTROLLER);
  const { isMobile } = Widget;

  const updateZoom = () => {
    const { zoomIsMinimum, zoomIsMaximum } = zoomController;

    setZoomState({
      zoomIsMaximum,
      zoomIsMinimum,
      zoomIndex: zoomController.currentZoomIndex,
    });
  };

  useEffect(() => {
    zoomController.addListener(zoomController.events.UPDATE, updateZoom);

    return () => {
      zoomController.removeListener(zoomController.events.UPDATE, updateZoom);
    };
  }, []);

  const zoomIn = () => {
    if (zoomController.isZooming || stateController.isDragging || stateController.isLocked) {
      return;
    }

    const { x, y } = Widget.getCenterOfScheme();

    zoomController.zoomIn({
      x,
      y,
      onEnd: () => {
        if (window.Widget.announcementsController) {
          window.Widget.announcementsController.activeItemId = null;
        }
        zoomController.updateZoomFromTheButton && zoomController.updateZoomFromTheButton();
      },
    });
  };

  const zoomOut = () => {
    if (zoomController.isZooming || stateController.isDragging || stateController.isLocked) {
      return;
    }

    const { x, y } = Widget.getCenterOfScheme();

    zoomController.zoomOut({
      x,
      y,
      onEnd: () => {
        if (window.Widget.announcementsController) {
          window.Widget.announcementsController.activeItemId = null;
        }
        zoomController.updateZoomFromTheButton && zoomController.updateZoomFromTheButton();
      },
    });
  };

  return { isMobile, zoomState, zoomIn, zoomOut, basketSize };
};

export default useZoom;
